import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/router";
import { Sidebar, SidebarContent, useSidebar } from "@src/lib/ui/Sidebar";
import AdminLeftNavOrgAndNewButtons from "@src/features/app/left-nav/admin/AdminLeftNavOrgAndNewButtons";
import AdminLeftNavMainMenu from "@src/features/app/left-nav/admin/AdminLeftNavMainMenu";
import AdminLeftNavLibraryMenu from "@src/features/app/left-nav/admin/AdminLeftNavLibraryMenu";
import AdminLeftNavPeopleMenu from "@src/features/app/left-nav/admin/AdminLeftNavPeopleMenu";
import AdminLeftNavWorkflowsMenu from "@src/features/app/left-nav/admin/AdminLeftNavWorkflowsMenu";
import AdminLeftNavUserFooter from "@src/features/app/left-nav/admin/AdminLeftNavUserFooter";
import AdminLeftNavSettingsNav from "@src/features/app/left-nav/admin/AdminLeftNavSettingsNav";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useUser from "@src/features/app/hooks/useUser";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import { useBillingInitialized } from "@src/features/settings/hooks/billing/useBillingInitialized";
import AdminPrimaryNavRail from "./AdminPrimaryNavRail";
import AdminSecondaryNavMenu from "./AdminSecondaryNavMenu";
import {
  AdminNavSectionId,
  adminAccountPlanNavItem,
  adminSettingsNavItem,
  findActivePrimaryItem,
  getVisiblePrimaryItems,
  getVisibleSecondaryItems,
} from "./adminNavConfig";
import { useIsAdminContentBuilderView } from "./useIsAdminContentBuilderView";

const AdminLeftNav: React.FC = () => {
  const { pathname } = useRouter();
  const { open, primaryRailPinned, isMobile, setOpen } = useSidebar();
  const wasContentBuilderView = useRef(false);
  const { userActionPermissions } = useActionPermissions();
  const { user } = useUser();
  const guestFeedbackEnabled = useFeatureFlag(Ff.GuestFeedbackCorrectiveAction);
  const guestFeedbackRecommendationsEnabled = useFeatureFlag(
    Ff.GuestFeedbackRecommendations,
  );
  const auditsEnabled = useFeatureFlag(Ff.Audits);
  const taskLibraryItemTypeEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const newMessagingEnabled = useFeatureFlag(Ff.NewMessaging);
  const pathsAsWorkflowsEnabled = useFeatureFlag(Ff.PathsAsWorkflows);
  const billingInitialized = useBillingInitialized({
    skip: !userActionPermissions?.manageBilling,
  });
  const isSettingsRoute =
    pathname.startsWith("/settings") &&
    pathname !== "/settings/archive" &&
    !pathname.startsWith("/settings/hris");
  const isContentBuilderView = useIsAdminContentBuilderView();

  useLayoutEffect(() => {
    if (isContentBuilderView) {
      if (!wasContentBuilderView.current) {
        wasContentBuilderView.current = true;
        setOpen(false);
      }
      return;
    }
    if (wasContentBuilderView.current) {
      wasContentBuilderView.current = false;
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- collapse only when entering builder
  }, [isContentBuilderView]);

  const visibilityContext = useMemo(
    () => ({
      permissions: userActionPermissions,
      featureFlags: {
        [Ff.Audits]: auditsEnabled,
        [Ff.TaskLibraryItemType]: taskLibraryItemTypeEnabled,
        [Ff.GuestFeedbackCorrectiveAction]: guestFeedbackEnabled,
        [Ff.GuestFeedbackRecommendations]: guestFeedbackRecommendationsEnabled,
        [Ff.NewMessaging]: newMessagingEnabled,
        [Ff.PathsAsWorkflows]: pathsAsWorkflowsEnabled,
      },
      billingInitialized,
      userType: user?.userType,
    }),
    [
      auditsEnabled,
      taskLibraryItemTypeEnabled,
      billingInitialized,
      guestFeedbackEnabled,
      guestFeedbackRecommendationsEnabled,
      newMessagingEnabled,
      pathsAsWorkflowsEnabled,
      userActionPermissions,
      user?.userType,
    ],
  );
  const visiblePrimaryItems = useMemo(
    () => getVisiblePrimaryItems(visibilityContext),
    [visibilityContext],
  );
  const routeActiveItem = useMemo(
    () => findActivePrimaryItem(pathname, visiblePrimaryItems),
    [pathname, visiblePrimaryItems],
  );
  const [selectedSectionId, setSelectedSectionId] = useState<AdminNavSectionId>(
    routeActiveItem.id,
  );

  useEffect(() => {
    setSelectedSectionId(routeActiveItem.id);
  }, [routeActiveItem.id]);

  const activeItem =
    selectedSectionId === "settings"
      ? adminSettingsNavItem
      : selectedSectionId === "accountPlan"
        ? adminAccountPlanNavItem
        : visiblePrimaryItems.find((item) => item.id === selectedSectionId) ||
          routeActiveItem;
  const visibleSecondaryItems = useMemo(
    () => getVisibleSecondaryItems(activeItem.items, visibilityContext),
    [activeItem.items, visibilityContext],
  );
  const hasSecondaryMenu =
    activeItem.id === "reporting" ||
    activeItem.id === "recommendations" ||
    visibleSecondaryItems.length > 0;

  const forcePrimaryRailExpanded = !hasSecondaryMenu;
  const railWidth =
    primaryRailPinned || forcePrimaryRailExpanded
      ? "var(--admin-primary-rail-expanded-width)"
      : "var(--admin-primary-rail-collapsed-width)";
  const reservedWidth = `calc(${railWidth} + ${
    hasSecondaryMenu && open ? "var(--admin-secondary-menu-width) + 8px" : "0px"
  })`;

  return (
    <>
      {isMobile && (
        <Sidebar collapsible="icon" variant="inset">
          {isSettingsRoute ? (
            <AdminLeftNavSettingsNav />
          ) : (
            <>
              <AdminLeftNavOrgAndNewButtons />
              <SidebarContent>
                <AdminLeftNavMainMenu />
                <AdminLeftNavLibraryMenu />
                <AdminLeftNavPeopleMenu />
                {userActionPermissions?.manageLibrary && (
                  <AdminLeftNavWorkflowsMenu />
                )}
              </SidebarContent>
            </>
          )}
          <AdminLeftNavUserFooter hideSettingsButton={isSettingsRoute} />
        </Sidebar>
      )}
      <div
        className="relative hidden h-svh shrink-0 transition-[width] duration-200 ease-out motion-reduce:transition-none md:block"
        style={{ width: reservedWidth }}
      >
        <AdminPrimaryNavRail
          items={visiblePrimaryItems}
          activeSectionId={activeItem.id}
          onSelectSection={setSelectedSectionId}
          forceExpanded={forcePrimaryRailExpanded}
        />
        {hasSecondaryMenu && (
          <AdminSecondaryNavMenu
            activeItem={activeItem}
            visibilityContext={visibilityContext}
          />
        )}
      </div>
    </>
  );
};

export default AdminLeftNav;
